/**
 * InfinitSales - Labels Page (Mock)
 * Manage conversation labels/tags
 */
import React, { useState } from 'react';
import { Tags, Plus, Edit2, Trash2, Search, PanelLeftOpen, PanelLeftClose, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const LabelsPage = ({ isNavOpen, onToggleNav }) => {
  const { colors, theme, toggleTheme } = useTheme();

  // Mock data
  const [labels] = useState([
    { id: 1, name: 'Novo Lead', color: '#25d366', count: 45 },
    { id: 2, name: 'Em Negociação', color: '#00a884', count: 23 },
    { id: 3, name: 'Aguardando Resposta', color: '#f59e0b', count: 12 },
    { id: 4, name: 'Cliente VIP', color: '#8b5cf6', count: 8 },
    { id: 5, name: 'Suporte', color: '#3b82f6', count: 34 },
    { id: 6, name: 'Finalizado', color: '#6b7280', count: 156 },
    { id: 7, name: 'Cancelado', color: '#ef4444', count: 18 },
  ]);

  const styles = {
    container: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.bgSecondary,
      height: '100vh',
      overflow: 'hidden',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 24px',
      backgroundColor: colors.bgPrimary,
      boxShadow: colors.shadowSm,
      minHeight: '60px',
      zIndex: 1,
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    navToggleBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      border: 'none',
      background: 'transparent',
      color: colors.textSecondary,
      borderRadius: '8px',
      cursor: 'pointer',
    },
    headerIcon: {
      width: '40px',
      height: '40px',
      backgroundColor: colors.accentPrimary,
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
    },
    headerTitle: {
      display: 'flex',
      flexDirection: 'column',
    },
    title: {
      fontSize: '18px',
      fontWeight: 600,
      color: colors.textPrimary,
      margin: 0,
    },
    subtitle: {
      fontSize: '13px',
      color: colors.textSecondary,
      margin: 0,
    },
    headerRight: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    headerBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      border: 'none',
      background: 'transparent',
      color: colors.textSecondary,
      borderRadius: '50%',
      cursor: 'pointer',
    },
    addBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 16px',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '13px',
      fontWeight: 500,
      cursor: 'pointer',
    },
    content: {
      flex: 1,
      padding: '24px',
      overflowY: 'auto',
    },
    searchBar: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: colors.bgPrimary,
      borderRadius: '8px',
      padding: '0 16px',
      marginBottom: '24px',
      border: `1px solid ${colors.borderColor}`,
    },
    searchInput: {
      flex: 1,
      padding: '12px',
      border: 'none',
      background: 'transparent',
      fontSize: '14px',
      color: colors.textPrimary,
      outline: 'none',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: '16px',
    },
    card: {
      backgroundColor: colors.bgPrimary,
      borderRadius: '12px',
      padding: '20px',
      border: `1px solid ${colors.borderColor}`,
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '12px',
    },
    labelInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    labelColor: {
      width: '16px',
      height: '16px',
      borderRadius: '4px',
    },
    labelName: {
      fontSize: '16px',
      fontWeight: 500,
      color: colors.textPrimary,
    },
    cardActions: {
      display: 'flex',
      gap: '8px',
    },
    actionBtn: {
      width: '32px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      background: 'transparent',
      color: colors.textSecondary,
      borderRadius: '6px',
      cursor: 'pointer',
    },
    labelCount: {
      fontSize: '14px',
      color: colors.textSecondary,
    },
    labelBar: {
      height: '4px',
      backgroundColor: colors.bgTertiary,
      borderRadius: '2px',
      marginTop: '12px',
      overflow: 'hidden',
    },
    labelBarFill: {
      height: '100%',
      borderRadius: '2px',
    },
  };

  const maxCount = Math.max(...labels.map(l => l.count));

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            style={styles.navToggleBtn}
            onClick={onToggleNav}
            title={isNavOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            {isNavOpen ? (
              <PanelLeftClose size={20} />
            ) : (
              <PanelLeftOpen size={20} />
            )}
          </button>
          <div style={styles.headerIcon}>
            <Tags size={20} />
          </div>
          <div style={styles.headerTitle}>
            <h1 style={styles.title}>Etiquetas</h1>
            <p style={styles.subtitle}>Gerencie as etiquetas das conversas</p>
          </div>
        </div>
        <div style={styles.headerRight}>
          <button style={styles.addBtn}>
            <Plus size={16} />
            Nova Etiqueta
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {/* Search */}
        <div style={styles.searchBar}>
          <Search size={18} color={colors.textTertiary} />
          <input
            type="text"
            placeholder="Buscar etiquetas..."
            style={styles.searchInput}
          />
        </div>

        {/* Labels Grid */}
        <div style={styles.grid}>
          {labels.map((label) => (
            <div key={label.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.labelInfo}>
                  <div style={{ ...styles.labelColor, backgroundColor: label.color }} />
                  <span style={styles.labelName}>{label.name}</span>
                </div>
                <div style={styles.cardActions}>
                  <button style={styles.actionBtn} title="Editar">
                    <Edit2 size={16} />
                  </button>
                  <button style={styles.actionBtn} title="Excluir">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <span style={styles.labelCount}>{label.count} conversas</span>
              <div style={styles.labelBar}>
                <div
                  style={{
                    ...styles.labelBarFill,
                    backgroundColor: label.color,
                    width: `${(label.count / maxCount) * 100}%`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LabelsPage;